import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FilePdf, FloppyDisk, UploadSimple, Camera, MapTrifold, ArrowsClockwise, PencilLine, Signature as SignIcon, X, PaperPlaneTilt, ArrowsLeftRight, Receipt } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SIG_SLOTS = [
  { k: "elaborado", label: "Elaborado por (Contratista)" },
  { k: "revisado", label: "Revisado por (Supervisión)" },
  { k: "aprobado", label: "Aprobado por (Cliente / Mina)" },
];

const emptyEstimate = () => ({
  folio: "",
  client_name: "", client_contract: "",
  project_id: "", project_name: "", area: "",
  issue_date: new Date().toISOString().slice(0, 10),
  date_from: new Date().toISOString().slice(0, 8) + "01",
  date_to: new Date().toISOString().slice(0, 10),
  concepts: [], descuentos: [],
  is_accumulative: false, previous_estimates: [],
  iva_pct: 16, currency: "MXN",
  signatures: {}, attachments: [],
  cfdi: { uuid: "", folio_fiscal: "", serie: "", fecha_timbrado: "", pac_provider: "", xml_url: "", pdf_url: "" },
  notes: "", status: "draft",
});

// ------------------ Signature drawing pad ------------------
function SignaturePad({ value, onChange, testid }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round";
    // pre-fill if value provided
    if (value) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, c.width, c.height); setEmpty(false); };
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { drawing.current = true; const p = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const p = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(p.x, p.y); ctx.stroke(); setEmpty(false); e.preventDefault(); };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const c = canvasRef.current;
    if (c) onChange(c.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true); onChange("");
  };

  return (
    <div>
      <canvas ref={canvasRef} width={220} height={80}
              className="w-full h-20 bg-slate-100 rounded border border-slate-200 touch-none cursor-crosshair"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid={testid} />
      <div className="flex justify-between items-center mt-1">
        <span className="text-[10px] text-slate-500">{empty ? "Firma con el mouse o dedo" : "✓ Firmado"}</span>
        <button type="button" onClick={clear} className="text-[10px] text-rose-600 hover:text-rose-600 underline">Limpiar</button>
      </div>
    </div>
  );
}

// ------------------ Main dialog ------------------
export default function ClientEstimateDialog({ estimateId, onClose, onSaved }) {
  const [est, setEst] = useState(estimateId ? null : emptyEstimate());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [uploadType, setUploadType] = useState("foto");
  const [uploadDesc, setUploadDesc] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [previousList, setPreviousList] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);

  // Load existing
  useEffect(() => {
    if (!estimateId) return;
    api.get(`/finance/client-estimates/${estimateId}`).then((r) => setEst(r.data)).catch((e) => {
      toast.error(apiError(e)); onClose();
    });
  }, [estimateId, onClose]);

  const upd = (k, v) => setEst((s) => ({ ...s, [k]: v }));
  const updSig = (slot, k, v) => setEst((s) => ({
    ...s, signatures: { ...s.signatures, [slot]: { ...(s.signatures?.[slot] || {}), [k]: v } }
  }));
  const updConcept = (idx, k, v) => setEst((s) => ({
    ...s, concepts: s.concepts.map((c, i) => i === idx ? { ...c, [k]: k === "qty" || k === "unit_price" ? parseFloat(v) || 0 : v } : c)
  }));
  const addConcept = () => setEst((s) => ({ ...s, concepts: [...s.concepts, { label: "", unit: "", qty: 0, unit_price: 0, notes: "" }] }));
  const delConcept = (idx) => setEst((s) => ({ ...s, concepts: s.concepts.filter((_, i) => i !== idx) }));
  const updDesc = (idx, k, v) => setEst((s) => ({
    ...s, descuentos: s.descuentos.map((d, i) => i === idx ? { ...d, [k]: k === "monto" ? parseFloat(v) || 0 : v } : d)
  }));
  const addDesc = () => setEst((s) => ({ ...s, descuentos: [...s.descuentos, { label: "", monto: 0, notes: "" }] }));
  const delDesc = (idx) => setEst((s) => ({ ...s, descuentos: s.descuentos.filter((_, i) => i !== idx) }));

  const updCfdi = (k, v) => setEst((s) => ({ ...s, cfdi: { ...(s.cfdi || {}), [k]: v } }));
  const updPrev = (idx, k, v) => setEst((s) => ({
    ...s, previous_estimates: s.previous_estimates.map((p, i) => i === idx ? { ...p, [k]: k === "amount_deducted" ? parseFloat(v) || 0 : v } : p)
  }));
  const delPrev = (idx) => setEst((s) => ({ ...s, previous_estimates: s.previous_estimates.filter((_, i) => i !== idx) }));

  // Load previous estimates whenever client_contract changes and is_accumulative is on
  useEffect(() => {
    if (!est?.is_accumulative || !est?.client_contract?.trim()) { setPreviousList([]); return; }
    const params = new URLSearchParams({ client_contract: est.client_contract });
    if (est.id) params.append("exclude_id", est.id);
    api.get(`/finance/client-estimates/previous-by-contract?${params.toString()}`)
      .then((r) => setPreviousList(r.data || []))
      .catch(() => setPreviousList([]));
  }, [est?.is_accumulative, est?.client_contract, est?.id]);

  const linkPrev = (p) => {
    if (est.previous_estimates.some((x) => x.id === p.id)) { toast.error("Ya está agregada"); return; }
    const amount = p.totals?.total || 0;
    setEst((s) => ({ ...s, previous_estimates: [...s.previous_estimates,
      { id: p.id, folio: p.folio, amount_deducted: amount, notes: `${p.date_from} → ${p.date_to}` }] }));
  };

  // Totals recomputed locally for the live footer
  const totals = (() => {
    const sub = (est?.concepts || []).reduce((a, c) => a + (parseFloat(c.qty) || 0) * (parseFloat(c.unit_price) || 0), 0);
    const desc = (est?.descuentos || []).reduce((a, d) => a + (parseFloat(d.monto) || 0), 0);
    const base = Math.max(sub - desc, 0);
    const iva = base * (parseFloat(est?.iva_pct) || 0) / 100;
    const total_bruto = base + iva;
    const previously_billed = (est?.previous_estimates || []).reduce((a, p) => a + (parseFloat(p.amount_deducted) || 0), 0);
    const total_neto = Math.max(total_bruto - previously_billed, 0);
    return { subtotal: sub, descuentos_total: desc, base, iva, total_bruto, previously_billed, total: total_neto };
  })();

  const loadFromRange = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/client-estimates/build-from-range?date_from=${est.date_from}&date_to=${est.date_to}`);
      setEst((s) => ({ ...s, concepts: r.data.concepts || [], descuentos: r.data.descuentos || [] }));
      toast.success(`Cargados ${r.data.concepts?.length || 0} conceptos de ${r.data.entries_count} entradas`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...est };
      delete clean.attachments;      // attachments managed separately
      delete clean.id; delete clean.totals;
      let saved;
      if (est.id) {
        const r = await api.put(`/finance/client-estimates/${est.id}`, clean);
        saved = r.data;
      } else {
        const r = await api.post("/finance/client-estimates", clean);
        saved = r.data;
      }
      toast.success(`Estimación ${saved.folio} guardada`);
      setEst(saved);
      onSaved?.(saved);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const uploadAttachment = async () => {
    if (!pendingFile) return;
    if (!est.id) { toast.error("Guarda la estimación antes de subir anexos"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("type", uploadType);
      fd.append("description", uploadDesc);
      // Let axios auto-set the multipart boundary
      await api.post(`/finance/client-estimates/${est.id}/attachments`, fd);
      // reload full doc to get updated attachments with data urls
      const r = await api.get(`/finance/client-estimates/${est.id}`);
      setEst(r.data);
      setPendingFile(null); setUploadDesc(""); if (fileRef.current) fileRef.current.value = "";
      toast.success("Anexo subido");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const removeAtt = async (aid) => {
    if (!window.confirm("¿Eliminar este anexo?")) return;
    try {
      await api.delete(`/finance/client-estimates/${est.id}/attachments/${aid}`);
      const r = await api.get(`/finance/client-estimates/${est.id}`);
      setEst(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async () => {
    if (!est.id) { toast.error("Guarda la estimación antes de generar el PDF"); return; }
    try {
      await downloadFile(`/finance/client-estimates/${est.id}/pdf`, `${est.folio || "estimacion"}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!est) return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md">
        <p className="text-slate-500 p-6 text-center">Cargando estimación…</p>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-600 flex items-center gap-3">
            <SignIcon size={22} weight="fill" />
            Estimación al Cliente {est.folio && <span className="text-sm text-slate-500 font-mono">· {est.folio}</span>}
            <span className="ml-auto text-[10px] uppercase tracking-widest bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-700">
              {est.status}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* ---- Header ---- */}
        <div className="grid md:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded p-3">
          <div><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Cliente / Mina</Label>
            <Input value={est.client_name} onChange={(e) => upd("client_name", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-client-name" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Contrato / OC</Label>
            <Input value={est.client_contract} onChange={(e) => upd("client_contract", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-contract" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Fecha de emisión</Label>
            <Input type="date" value={est.issue_date} onChange={(e) => upd("issue_date", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-issue-date" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Proyecto</Label>
            <Input value={est.project_name} onChange={(e) => upd("project_name", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Área / Nivel</Label>
            <Input value={est.area} onChange={(e) => upd("area", e.target.value)}
              className="bg-white border-slate-200 text-slate-900" /></div>
          <div className="flex gap-2">
            <div className="flex-1"><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Desde</Label>
              <Input type="date" value={est.date_from} onChange={(e) => upd("date_from", e.target.value)}
                className="bg-white border-slate-200 text-slate-900" data-testid="ce-date-from" /></div>
            <div className="flex-1"><Label className="text-[10px] text-slate-500 uppercase tracking-wider">Hasta</Label>
              <Input type="date" value={est.date_to} onChange={(e) => upd("date_to", e.target.value)}
                className="bg-white border-slate-200 text-slate-900" data-testid="ce-date-to" /></div>
          </div>
        </div>

        {/* ---- Concepts ---- */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-bold text-emerald-700 uppercase text-xs tracking-widest">Conceptos a cobrar</h4>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={loadFromRange} disabled={busy}
                className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-amber-600 h-8 gap-1"
                data-testid="ce-load-range">
                <ArrowsClockwise size={12} weight="bold" /> Cargar producción del rango
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addConcept}
                className="border-slate-200 text-slate-800 hover:bg-slate-100 h-8 gap-1" data-testid="ce-add-concept">
                <Plus size={12} weight="bold" /> Concepto manual
              </Button>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                  <th className="px-2 py-1">Descripción</th>
                  <th className="px-2 py-1">U.</th>
                  <th className="px-2 py-1 text-right">Cantidad</th>
                  <th className="px-2 py-1 text-right">P. Unit.</th>
                  <th className="px-2 py-1 text-right">Importe</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {est.concepts.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-500 italic">Sin conceptos. Usa «Cargar producción» o añade manual.</td></tr>}
                {est.concepts.map((c, idx) => (
                  <tr key={idx} className="border-b border-slate-100/60" data-testid={`ce-concept-row-${idx}`}>
                    <td className="px-2 py-1"><Input value={c.label} onChange={(e) => updConcept(idx, "label", e.target.value)}
                      className="h-7 bg-white border-slate-200 text-slate-900 text-xs" /></td>
                    <td className="px-2 py-1 w-16"><Input value={c.unit} onChange={(e) => updConcept(idx, "unit", e.target.value)}
                      className="h-7 bg-white border-slate-200 text-slate-900 text-xs" /></td>
                    <td className="px-2 py-1 w-24"><Input type="number" step="0.01" value={c.qty} onChange={(e) => updConcept(idx, "qty", e.target.value)}
                      className="h-7 bg-white border-slate-200 text-slate-900 text-xs text-right font-mono" data-testid={`ce-qty-${idx}`} /></td>
                    <td className="px-2 py-1 w-28"><Input type="number" step="0.01" value={c.unit_price} onChange={(e) => updConcept(idx, "unit_price", e.target.value)}
                      className="h-7 bg-white border-slate-200 text-slate-900 text-xs text-right font-mono" data-testid={`ce-price-${idx}`} /></td>
                    <td className="px-2 py-1 text-right font-bold text-emerald-700">{money((parseFloat(c.qty) || 0) * (parseFloat(c.unit_price) || 0))}</td>
                    <td className="px-2 py-1 w-8"><button type="button" onClick={() => delConcept(idx)} className="text-slate-500 hover:text-rose-600"><Trash size={13} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---- Descuentos ---- */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-bold text-rose-600 uppercase text-xs tracking-widest">Descuentos / penalizaciones</h4>
            <Button type="button" variant="outline" size="sm" onClick={addDesc}
              className="border-slate-200 text-slate-800 hover:bg-slate-100 h-8 gap-1" data-testid="ce-add-desc">
              <Plus size={12} weight="bold" /> Añadir
            </Button>
          </div>
          {est.descuentos.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                  <th className="px-2 py-1">Concepto</th><th className="px-2 py-1">Notas</th><th className="px-2 py-1 text-right">Monto</th><th></th>
                </tr></thead>
                <tbody>{est.descuentos.map((d, idx) => (
                  <tr key={idx} className="border-b border-slate-100/60">
                    <td className="px-2 py-1"><Input value={d.label} onChange={(e) => updDesc(idx, "label", e.target.value)} className="h-7 bg-white border-slate-200 text-slate-900 text-xs" /></td>
                    <td className="px-2 py-1"><Input value={d.notes || ""} onChange={(e) => updDesc(idx, "notes", e.target.value)} className="h-7 bg-white border-slate-200 text-slate-900 text-xs" /></td>
                    <td className="px-2 py-1 w-28"><Input type="number" step="0.01" value={d.monto} onChange={(e) => updDesc(idx, "monto", e.target.value)} className="h-7 bg-white border-slate-200 text-slate-900 text-xs text-right font-mono" /></td>
                    <td className="px-2 py-1 w-8"><button type="button" onClick={() => delDesc(idx)} className="text-slate-500 hover:text-rose-600"><Trash size={13} /></button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---- Totals ---- */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            <div><span className="text-slate-500">Subtotal</span><br/><b className="text-slate-800 text-sm">{money(totals.subtotal)}</b></div>
            <div><span className="text-slate-500">(–) Desc.</span><br/><b className="text-rose-600 text-sm">-{money(totals.descuentos_total)}</b></div>
            <div><span className="text-slate-500">Base gravable</span><br/><b className="text-slate-800 text-sm">{money(totals.base)}</b></div>
            <div className="flex gap-1 items-end"><div><span className="text-slate-500">IVA</span><br/><b className="text-slate-800 text-sm">{money(totals.iva)}</b></div>
              <Input type="number" step="0.01" value={est.iva_pct} onChange={(e) => upd("iva_pct", parseFloat(e.target.value) || 0)}
                className="h-6 w-14 bg-white border-slate-200 text-slate-900 text-xs text-right" title="% IVA" /></div>
            {est.is_accumulative && totals.previously_billed > 0 ? (
              <div><span className="text-slate-500">(–) Acumulado</span><br/><b className="text-orange-600 text-sm">-{money(totals.previously_billed)}</b></div>
            ) : (
              <div><span className="text-slate-500">Bruto</span><br/><b className="text-slate-700 text-sm">{money(totals.total_bruto)}</b></div>
            )}
            <div><span className="text-amber-600 uppercase text-[10px] font-bold">TOTAL NETO</span><br/><b className="text-amber-600 text-xl font-heading" data-testid="ce-total">{money(totals.total)}</b></div>
          </div>
        </div>

        {/* ---- Estimaciones acumuladas ---- */}
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded p-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={est.is_accumulative} onChange={(e) => upd("is_accumulative", e.target.checked)}
              className="accent-orange-500" data-testid="ce-toggle-accumulative" />
            <ArrowsLeftRight size={14} weight="bold" className="text-orange-600" />
            <span className="font-heading font-bold text-orange-600 uppercase text-xs tracking-widest">Estimación acumulada</span>
            <span className="text-[10px] text-slate-500">— deducir estimaciones previas del mismo contrato</span>
          </label>
          {est.is_accumulative && (
            <div className="mt-3 space-y-2">
              {est.previous_estimates?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest border-b border-slate-200">
                      <th className="px-2 py-1">Folio</th>
                      <th className="px-2 py-1">Notas</th>
                      <th className="px-2 py-1 text-right">Monto a deducir</th>
                      <th></th>
                    </tr></thead>
                    <tbody>
                      {est.previous_estimates.map((p, idx) => (
                        <tr key={p.id + idx} className="border-b border-slate-100/60" data-testid={`ce-prev-row-${idx}`}>
                          <td className="px-2 py-1 font-mono text-amber-700">{p.folio || p.id.slice(0, 8)}</td>
                          <td className="px-2 py-1"><Input value={p.notes || ""} onChange={(e) => updPrev(idx, "notes", e.target.value)} className="h-7 bg-white border-slate-200 text-slate-900 text-xs" /></td>
                          <td className="px-2 py-1 w-32"><Input type="number" step="0.01" value={p.amount_deducted} onChange={(e) => updPrev(idx, "amount_deducted", e.target.value)} className="h-7 bg-white border-slate-200 text-slate-900 text-xs text-right font-mono" data-testid={`ce-prev-amount-${idx}`} /></td>
                          <td className="px-2 py-1 w-8"><button type="button" onClick={() => delPrev(idx)} className="text-slate-500 hover:text-rose-600"><Trash size={13} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {previousList.length > 0 ? (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Estimaciones previas del contrato «{est.client_contract}»</p>
                  <div className="grid md:grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                    {previousList.map((p) => {
                      const already = est.previous_estimates.some((x) => x.id === p.id);
                      return (
                        <button key={p.id} type="button" disabled={already}
                          onClick={() => linkPrev(p)}
                          data-testid={`ce-prev-add-${p.id}`}
                          className={`text-left text-[11px] rounded p-2 border ${already ? "bg-white border-slate-200 text-slate-600" : "bg-white border-slate-200 hover:border-orange-500 text-slate-800"}`}>
                          <b className="font-mono text-amber-700">{p.folio}</b> · <span className="text-slate-500">{p.date_from} → {p.date_to}</span>
                          <div className="text-orange-600 font-bold">{money(p.totals?.total || 0)}</div>
                          {already && <span className="text-[9px] text-emerald-700">✓ ya agregada</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 italic">
                  {est.client_contract ? "Sin estimaciones previas para este contrato." : "Introduce el número de contrato arriba para ver estimaciones previas."}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ---- CFDI manual ---- */}
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <Receipt size={14} weight="bold" className="text-emerald-700" />
            <h4 className="font-heading font-bold text-emerald-700 uppercase text-xs tracking-widest">Timbrado CFDI (México) · Manual</h4>
            <span className="text-[10px] text-slate-500">— captura tras timbrar en tu PAC</span>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <div><Label className="text-[10px] text-slate-500 uppercase">UUID (Folio Fiscal SAT)</Label>
              <Input value={est.cfdi?.uuid || ""} onChange={(e) => updCfdi("uuid", e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000" className="h-8 bg-white border-slate-200 text-slate-900 text-xs font-mono" data-testid="ce-cfdi-uuid" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Serie</Label>
              <Input value={est.cfdi?.serie || ""} onChange={(e) => updCfdi("serie", e.target.value)}
                className="h-8 bg-white border-slate-200 text-slate-900 text-xs" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Folio Fiscal / Interno</Label>
              <Input value={est.cfdi?.folio_fiscal || ""} onChange={(e) => updCfdi("folio_fiscal", e.target.value)}
                className="h-8 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ce-cfdi-folio" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Fecha de timbrado</Label>
              <Input type="date" value={est.cfdi?.fecha_timbrado?.slice(0, 10) || ""} onChange={(e) => updCfdi("fecha_timbrado", e.target.value)}
                className="h-8 bg-white border-slate-200 text-slate-900 text-xs" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">PAC (proveedor)</Label>
              <Input value={est.cfdi?.pac_provider || ""} onChange={(e) => updCfdi("pac_provider", e.target.value)}
                placeholder="Facturama, SW Sapien, etc." className="h-8 bg-white border-slate-200 text-slate-900 text-xs" /></div>
            <div><Label className="text-[10px] text-slate-500 uppercase">Enlace XML / PDF (opcional)</Label>
              <Input value={est.cfdi?.pdf_url || ""} onChange={(e) => updCfdi("pdf_url", e.target.value)}
                placeholder="https://…" className="h-8 bg-white border-slate-200 text-slate-900 text-xs" /></div>
          </div>
          {est.cfdi?.uuid && (
            <p className="text-[10px] text-emerald-700 mt-2">✓ CFDI capturado — el UUID se imprimirá en el PDF de estimación.</p>
          )}
        </div>

        {/* ---- Notes ---- */}
        <div className="mt-4">
          <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Notas / justificación</Label>
          <Textarea rows={2} value={est.notes} onChange={(e) => upd("notes", e.target.value)}
            className="bg-white border-slate-200 text-slate-900" data-testid="ce-notes" />
        </div>

        {/* ---- Signatures ---- */}
        <div className="mt-4">
          <h4 className="font-heading font-bold text-sky-700 uppercase text-xs tracking-widest mb-2">Firmas</h4>
          <div className="grid md:grid-cols-3 gap-3">
            {SIG_SLOTS.map(({ k, label }) => {
              const s = est.signatures?.[k] || {};
              return (
                <div key={k} className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid={`ce-sig-${k}`}>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</p>
                  <Input placeholder="Nombre completo" value={s.name || ""} onChange={(e) => updSig(k, "name", e.target.value)}
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs" data-testid={`ce-sig-name-${k}`} />
                  <Input placeholder="Cargo / puesto" value={s.position || ""} onChange={(e) => updSig(k, "position", e.target.value)}
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs" />
                  <Input type="date" value={s.date || ""} onChange={(e) => updSig(k, "date", e.target.value)}
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs" />
                  <SignaturePad value={s.image || ""} onChange={(v) => updSig(k, "image", v)} testid={`ce-sig-canvas-${k}`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- Attachments ---- */}
        <div className="mt-4">
          <h4 className="font-heading font-bold text-violet-600 uppercase text-xs tracking-widest mb-2">Anexos · Fotos y Planos</h4>
          {!est.id && <p className="text-[11px] text-amber-600 italic mb-2">Guarda primero la estimación para poder subir anexos.</p>}
          {est.id && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <div className="grid md:grid-cols-4 gap-2 items-end">
                <div><Label className="text-[10px] text-slate-500 uppercase">Tipo</Label>
                  <Select value={uploadType} onValueChange={setUploadType}>
                    <SelectTrigger className="h-8 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ce-att-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-900">
                      <SelectItem value="foto">Foto evidencia</SelectItem>
                      <SelectItem value="plano">Plano / esquema</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="md:col-span-2"><Label className="text-[10px] text-slate-500 uppercase">Descripción</Label>
                  <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                    placeholder="ej. Fortificación instalada rampa -450"
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs" data-testid="ce-att-desc" /></div>
                <div><Label className="text-[10px] text-slate-500 uppercase">Archivo</Label>
                  <Input type="file" ref={fileRef} onChange={(e) => setPendingFile(e.target.files?.[0])}
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif"
                    className="h-8 bg-white border-slate-200 text-slate-900 text-xs file:text-slate-900 file:bg-slate-700 file:border-0" data-testid="ce-att-file" /></div>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={uploadAttachment} disabled={uploading || !pendingFile}
                  className="bg-violet-500 hover:bg-violet-400 text-slate-900 font-bold gap-1.5 h-8" data-testid="ce-att-upload">
                  <UploadSimple size={13} weight="bold" /> {uploading ? "Subiendo…" : "Subir anexo"}
                </Button>
              </div>
              {est.attachments?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {est.attachments.map((a) => (
                    <div key={a.id} className="bg-white border border-slate-200 rounded overflow-hidden" data-testid={`ce-att-${a.id}`}>
                      <img src={a.data} alt={a.name} className="w-full h-24 object-cover" />
                      <div className="p-2 text-[10px]">
                        <div className="flex items-center gap-1">
                          {a.type === "plano" ? <MapTrifold size={11} className="text-sky-700" /> : a.type === "foto" ? <Camera size={11} className="text-emerald-700" /> : <PencilLine size={11} className="text-slate-500" />}
                          <span className="font-bold text-slate-700 uppercase text-[9px]">{a.type}</span>
                          <button onClick={() => removeAtt(a.id)} className="ml-auto text-slate-500 hover:text-rose-600"><X size={11} /></button>
                        </div>
                        <p className="text-slate-500 truncate mt-1">{a.description || a.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- Footer actions ---- */}
        <div className="mt-5 flex flex-wrap gap-2 justify-end sticky bottom-0 bg-white pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cerrar</Button>
          {est.id && (
            <Button onClick={downloadPdf} variant="outline"
              className="border-amber-800 text-amber-600 hover:bg-amber-500 hover:text-slate-900 gap-1.5" data-testid="ce-pdf">
              <FilePdf size={14} weight="bold" /> Generar PDF
            </Button>
          )}
          {est.id && (
            <Button onClick={() => setSendOpen(true)} variant="outline"
              className="border-sky-800 text-sky-700 hover:bg-sky-500 hover:text-slate-900 gap-1.5" data-testid="ce-send">
              <PaperPlaneTilt size={14} weight="bold" /> Enviar al cliente
            </Button>
          )}
          <Button onClick={save} disabled={busy}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-1.5" data-testid="ce-save">
            <FloppyDisk size={14} weight="bold" /> {busy ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </DialogContent>
      {sendOpen && (
        <SendEstimateDialog estimate={est} onClose={() => setSendOpen(false)}
          onSent={() => { setSendOpen(false); onSaved?.(est); /* reload */
            api.get(`/finance/client-estimates/${est.id}`).then((r) => setEst(r.data)).catch(() => {});
          }} />
      )}
    </Dialog>
  );
}

// ------------------ Send dialog (Resend) ------------------
function SendEstimateDialog({ estimate, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`Estimación ${estimate.folio} · ${estimate.client_name || ""}`);
  const [message, setMessage] = useState("Adjuntamos la estimación del período para su revisión y trámite de pago.");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const emails = to.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Añade al menos un correo"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/finance/client-estimates/${estimate.id}/send`, { to: emails, subject, message, include_annexes: true });
      toast.success(`Enviado a ${r.data.to?.length || emails.length} destinatario(s)`);
      onSent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-sky-700 flex items-center gap-2">
          <PaperPlaneTilt size={20} weight="fill" /> Enviar estimación al cliente
        </DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-2">Se enviará por correo con el PDF adjunto (incluye firmas y anexos). Se marcará como <b>Enviada</b>.</p>
        <div className="space-y-3 mt-2">
          <div><Label className="text-[10px] text-slate-500 uppercase">Destinatarios (separados por coma)</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="cliente@minera.com, contralor@minera.com"
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-send-to" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase">Asunto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-send-subject" /></div>
          <div><Label className="text-[10px] text-slate-500 uppercase">Mensaje</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              className="bg-white border-slate-200 text-slate-900" data-testid="ce-send-message" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 hover:bg-slate-50">Cancelar</Button>
          <Button onClick={send} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold gap-1.5" data-testid="ce-send-confirm">
            <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Enviando…" : "Enviar ahora"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
